import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Link2, Search, XCircle } from 'lucide-react';
import {
  comparePurchaseOrder,
  formatMoney,
  invoiceBilledCurrency,
  type Invoice,
  type PurchaseOrder,
  type PurchaseOrderComparedField,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { Card, ErrorState, Money } from '@/components/ui';

/** How each compared field is named on screen. */
function fieldLabel(field: PurchaseOrderComparedField, currency: string | undefined): string {
  if (field === 'vendorGstin') return currency === 'INR' ? 'Vendor GSTIN' : 'Vendor tax ID';
  return {
    currency: 'Currency',
    subtotal: 'Taxable Value',
    taxAmount: 'Tax',
    totalAmount: 'Total',
  }[field];
}

/**
 * Purchase-order matching against the Elixir API.
 *
 * Sits above the raw extracted form. The reviewer types the PO number printed
 * on the invoice and fetches that one PO from Elixir; it is compared field by
 * field with the invoice and can then be linked. Entirely optional — an
 * invoice with no PO submits exactly as before.
 */
export function PurchaseOrderMatch({ invoice, editable }: { invoice: Invoice; editable: boolean }) {
  const queryClient = useQueryClient();
  const linked = invoice.purchaseOrder;
  const [poNumber, setPoNumber] = useState(linked?.poNumber ?? '');
  // A linked PO is fetched on arrival so its details and chips are on screen;
  // otherwise nothing is fetched until the reviewer asks.
  const [fetched, setFetched] = useState<string | null>(linked?.poNumber ?? null);
  const [formError, setFormError] = useState<string | null>(null);

  const result = useQuery({
    queryKey: ['invoice-po-lookup', invoice.id, fetched],
    queryFn: () => api.invoices.lookupPurchaseOrder(invoice.id, fetched!),
    enabled: fetched !== null,
    retry: false,
    staleTime: 60_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });
  const link = useMutation({
    mutationFn: (number: string) => api.invoices.linkPurchaseOrder(invoice.id, number),
    onSuccess: refresh,
  });
  const unlink = useMutation({
    mutationFn: () => api.invoices.unlinkPurchaseOrder(invoice.id),
    onSuccess: refresh,
  });

  const fetchPo = () => {
    const value = poNumber.trim();
    if (!value) {
      setFormError('Enter a PO number');
      return;
    }
    setFormError(null);
    setFetched(value);
  };

  const po: PurchaseOrder | null | undefined = result.data?.purchaseOrder;
  const billed = invoiceBilledCurrency(invoice);
  // A value saved at link time is in the invoice's currency, except a
  // currency mismatch, which is itself text.
  const show = (value: string | number) =>
    typeof value === 'number' ? formatMoney(value, invoice.currency as never) : value;

  return (
    <Card className="mb-6 p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold">Purchase order match</h2>
          <span className="badge bg-ink-100 text-ink-600">Optional</span>
        </div>
        <span className="text-xs text-ink-500">Looked up in the Elixir API</span>
      </div>

      {linked ? (
        <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="flex flex-wrap items-center gap-2">
              <Link2 className="h-4 w-4" aria-hidden="true" />
              This invoice is matched with PO <strong>{linked.poNumber}</strong>
              {linked.poRevision ? <span>rev {linked.poRevision}</span> : null}
              <span className="text-emerald-700">
                · {linked.poDate ? formatDate(linked.poDate) : 'No date'} ·{' '}
                {linked.status ?? 'Status unknown'} · linked {formatDateTime(linked.linkedAt)}
                {linked.linkedByName ? ` by ${linked.linkedByName}` : ''}
              </span>
            </span>
            {editable ? (
              <button
                className="btn-secondary"
                disabled={unlink.isPending}
                onClick={() => unlink.mutate()}
              >
                {unlink.isPending ? 'Removing…' : 'Unlink'}
              </button>
            ) : null}
          </div>
          {/* Only what disagreed was kept when the PO was linked. */}
          {linked.mismatches.length === 0 ? (
            <p className="mt-2 text-emerald-800">
              Every compared field matched when it was linked.
            </p>
          ) : (
            <ul className="mt-2 space-y-1 text-amber-900" aria-label="Mismatches when linked">
              {linked.mismatches.map((mismatch) => (
                <li key={mismatch.field}>
                  {fieldLabel(mismatch.field, invoice.currency)} differs: PO{' '}
                  {show(mismatch.poValue)} · invoice {show(mismatch.invoiceValue)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          fetchPo();
        }}
      >
        <label className="block w-full sm:w-72">
          <span className="label">PO number</span>
          <input
            className="input"
            value={poNumber}
            maxLength={60}
            placeholder="e.g. SFPO2300000002"
            onChange={(event) => setPoNumber(event.target.value)}
          />
        </label>
        <button type="submit" className="btn-secondary" disabled={result.isFetching}>
          <Search className="h-4 w-4" aria-hidden="true" />
          {result.isFetching ? 'Fetching…' : 'Fetch PO'}
        </button>
      </form>
      {formError ? <p className="mt-2 text-sm text-red-700">{formError}</p> : null}

      <div className="mt-4">
        {result.error ? (
          <ErrorState error={result.error} compact />
        ) : result.isFetching && !result.data ? (
          <p className="text-sm text-ink-500">Fetching from the Elixir API…</p>
        ) : result.data && !po ? (
          <p className="text-sm text-ink-500">
            No purchase order <strong>{fetched}</strong> was found in the Elixir API. Check the
            number and fetch again.
          </p>
        ) : fetched === null ? (
          <p className="text-sm text-ink-500">
            Enter the PO number printed on the invoice and fetch it to compare.
          </p>
        ) : null}
      </div>

      {po ? (
        <PurchaseOrderPreview
          po={po}
          invoice={invoice}
          action={
            // A PO in another currency than the bill cannot back it; the
            // server refuses it too, so the button is not offered. The bill's
            // currency is the document's (a dollar bill on a rupee company is
            // still in dollars).
            po.currency !== billed ? (
              <span className="text-sm text-amber-800">
                PO is in {po.currency}, invoice is billed in {billed} — cannot link
              </span>
            ) : editable && linked?.poNumber !== po.poNumber ? (
              <button
                className="btn-primary"
                disabled={link.isPending}
                onClick={() => link.mutate(po.poNumber)}
              >
                {link.isPending
                  ? 'Linking…'
                  : linked
                    ? `Replace with ${po.poNumber}`
                    : 'Link to invoice'}
              </button>
            ) : null
          }
        />
      ) : null}
      {link.error ? (
        <div className="mt-3">
          <ErrorState error={link.error} compact />
        </div>
      ) : null}
      {unlink.error ? (
        <div className="mt-3">
          <ErrorState error={unlink.error} compact />
        </div>
      ) : null}
    </Card>
  );
}

function PurchaseOrderPreview({
  po,
  invoice,
  action,
}: {
  po: PurchaseOrder;
  invoice: Invoice;
  action: React.ReactNode;
}) {
  // The same comparison the server runs when linking, so a red chip here is
  // exactly a mismatch that will be kept on the invoice.
  const checks = comparePurchaseOrder(po, invoice).map((check) => ({
    label: fieldLabel(check.field, invoice.currency),
    ok: check.ok,
  }));

  return (
    <div className="mt-4 rounded-md border border-ink-100 p-4" aria-label="Selected purchase order">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-ink-900">
            {po.poNumber}
            {po.poRevision ? <span className="text-ink-500"> rev {po.poRevision}</span> : null}
          </p>
          <p className="text-sm text-ink-500">
            {po.poDate ? formatDate(po.poDate) : 'No date'} · {po.status ?? 'Status unknown'} ·{' '}
            {po.currency}
          </p>
        </div>
        {action}
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {checks.map((check) => (
          <span
            key={check.label}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
              check.ok === null
                ? 'bg-ink-100 text-ink-600'
                : check.ok
                  ? 'bg-emerald-50 text-emerald-800'
                  : 'bg-amber-50 text-amber-800'
            }`}
          >
            {check.ok === null ? null : check.ok ? (
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {check.label}{' '}
            {check.ok === null ? 'not compared' : check.ok ? 'matches invoice' : 'differs'}
          </span>
        ))}
      </div>

      <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-ink-500">Vendor</dt>
          <dd>{po.vendorName ?? '—'}</dd>
          <dd className="text-xs text-ink-500">{po.vendorGstin ?? ''}</dd>
        </div>
        <div>
          <dt className="text-ink-500">Buyer</dt>
          <dd>{po.buyerName ?? '—'}</dd>
          <dd className="text-xs text-ink-500">{po.buyerGstin ?? ''}</dd>
        </div>
        <div>
          <dt className="text-ink-500">Taxable Value · Tax</dt>
          <dd>
            <Money minor={po.subtotal} currency={po.currency} /> ·{' '}
            <Money minor={po.taxAmount} currency={po.currency} />
          </dd>
        </div>
        <div>
          <dt className="text-ink-500">PO total</dt>
          <dd className="font-semibold">
            <Money minor={po.totalAmount} currency={po.currency} />
          </dd>
        </div>
      </dl>

      {po.lines.length ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-ink-500">
              <tr>
                <th className="py-1 pr-3 font-medium">#</th>
                <th className="py-1 pr-3 font-medium">Item</th>
                <th className="py-1 pr-3 font-medium">HSN/SAC</th>
                <th className="py-1 pr-3 text-right font-medium">Qty</th>
                <th className="py-1 pr-3 text-right font-medium">Unit price</th>
                <th className="py-1 pr-3 text-right font-medium">Tax</th>
                <th className="py-1 pr-3 text-right font-medium">Line total</th>
                <th className="py-1 font-medium">GRN</th>
              </tr>
            </thead>
            <tbody>
              {po.lines.map((line, index) => (
                <tr key={line.lineNumber ?? index} className="border-t border-ink-100">
                  <td className="py-1 pr-3">{line.lineNumber ?? index + 1}</td>
                  <td className="py-1 pr-3">{line.itemCode ?? line.description ?? '—'}</td>
                  <td className="py-1 pr-3">{line.hsnSac ?? '—'}</td>
                  <td className="py-1 pr-3 text-right">
                    {line.quantityOrdered ?? '—'} {line.uom ?? ''}
                  </td>
                  <td className="py-1 pr-3 text-right">
                    <Money minor={line.unitPrice} currency={po.currency} />
                  </td>
                  <td className="py-1 pr-3 text-right">
                    <Money minor={line.taxAmount} currency={po.currency} />
                    {line.taxRatePercent ? (
                      <span className="text-ink-500"> ({line.taxRatePercent}%)</span>
                    ) : null}
                  </td>
                  <td className="py-1 pr-3 text-right">
                    <Money minor={line.lineTotal} currency={po.currency} />
                  </td>
                  <td className="py-1">{line.grnNumbers.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
